import { addDoc, collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../../shared/utils/firebase';
import { DashboardClient, Programme, TaskKind } from './types';

/**
 * Google Chat space that hears about cleared alerts. Moved here unchanged from
 * the old Dashboard; it ships in the client bundle, so it should move behind a
 * Cloud Function.
 */
const ALERT_CLEARED_WEBHOOK =
  'https://chat.googleapis.com/v1/spaces/AAQAtmpFPPU/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=ug_d7F2-7iYI6zUwOz56GDNreewNQlZB0qYNZ4kfbLg';

const WEBHOOK_TIMEOUT_MS = 10 * 1000;

export interface Actor {
  uid: string;
  name: string;
}

/** Why a task was cleared. Stored verbatim (via clearReasonText), so keep the wording stable. */
export const CLEAR_REASONS = ['Done outside the system', 'Client declined', 'Not applicable to this client', 'Other'] as const;
export type ClearReason = (typeof CLEAR_REASONS)[number];

/** The free-text reason stored on the cleared record: the chosen reason, plus the note when there is one. */
export const clearReasonText = (reason: ClearReason, note: string) => (note.trim() ? `${reason}: ${note.trim()}` : reason);

/**
 * Records one alert reason as cleared (ignition_alerts_cleared or
 * foundation_alerts_cleared, as the old dashboard did) and tells the team chat.
 * Firestore rules allow this for managers and admins only.
 */
export async function clearAlertReason(
  client: Pick<DashboardClient, 'clientId' | 'name'>,
  programme: Programme,
  reason: string,
  clearReason: string,
  actor: Actor
): Promise<void> {
  await setDoc(doc(collection(db, `${programme}_alerts_cleared`)), {
    clientId: client.clientId,
    clientName: client.name,
    alertReasons: [reason],
    clearReason,
    clearedBy: actor.uid,
    clearedByName: actor.name,
    clearedAt: serverTimestamp(),
  });
  const heading = programme === 'foundation' ? 'Foundation Alert Cleared' : 'Alert Cleared';
  // Not awaited: the cleared record is what matters, and a slow chat call must not hold the task or a sign-out.
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), WEBHOOK_TIMEOUT_MS) : null;
  fetch(ALERT_CLEARED_WEBHOOK, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text: `🚨 *${heading}*\n\n*Client:* ${client.name}\n*Alerts Cleared:*\n• ${reason}\n\n*Cleared by:* ${actor.name}\n*Reason:* ${clearReason}`,
    }),
    signal: controller?.signal,
  })
    .catch((error) => console.error('Dashboard: cleared-alert chat notification failed', error))
    .finally(() => {
      if (timer) clearTimeout(timer);
    });
}

/** Saves a check-in, retrying briefly as the old dashboard shortcut did. */
export async function logCheckIn(clientId: string, type: '2_week' | '10_week', notes: string, actor: Actor, attempts = 3): Promise<void> {
  const payload = {
    client_id: clientId,
    check_in_type: type,
    response: notes.trim(),
    submitted_by: actor.uid,
    submitted_by_name: actor.name,
    submitted_at: serverTimestamp(),
  };
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      await addDoc(collection(db, 'client_check_ins'), payload);
      return;
    } catch (error) {
      lastError = error;
      if (attempt < attempts) await new Promise((resolve) => setTimeout(resolve, attempt * 500));
    }
  }
  throw lastError;
}

/**
 * Tasks done in another tab of the client's page. In the detail panel the whole
 * notice links there, named by the tab; other kinds have no link of their own
 * (the panel's header opens the profile).
 */
export const DESTINATION_TABS: Partial<Record<TaskKind, string>> = { report: 'Reports', assessment: 'Assessments', success: 'Success' };
